import { isVue3 } from 'virtual-vue';
import type { DatePickerColumnType } from './types';

type InputMode = 'array' | 'string' | 'date';

interface UseDateFormatOptions {
  columnsType: () => DatePickerColumnType[];
  format: () => string;
  modelValue: () => string[] | string | Date;
  value: () => string[] | string | Date;
}

/**
 * DatePicker 的格式化与 v-model 兼容层 composable。
 *
 * 职责：
 * 1. 在 string[] / string / Date 三种 v-model 格式之间互转
 * 2. 基于 format 模板进行字符串的格式化与解析
 */
export function useDateFormat(options: UseDateFormatOptions) {
  const { columnsType, format, modelValue, value } = options;

  const getRawValue = (): string[] | string | Date => (
    isVue3 ? modelValue() : value()
  );

  const getInputMode = (): InputMode => {
    const raw = getRawValue();
    if (raw instanceof Date) return 'date';
    if (typeof raw === 'string') return 'string';
    return 'array';
  };

  /** 将内部 string[]（按 columnsType 顺序）填入 format 模板 */
  const formatValues = (vals: string[]): string => {
    const types = columnsType();
    const map: Record<string, string> = {};
    types.forEach((type: DatePickerColumnType, idx: number) => {
      if (type === 'year') map.YYYY = vals[idx] || '';
      if (type === 'month') map.MM = vals[idx] || '';
      if (type === 'day') map.DD = vals[idx] || '';
    });
    return format()
      .replace('YYYY', map.YYYY || '')
      .replace('MM', map.MM || '')
      .replace('DD', map.DD || '');
  };

  /** 从 format 模板解析字符串，提取各字段并按 columnsType 顺序返回 string[] */
  const parseFormatted = (str: string): string[] => {
    const fmt = format();
    const tokenOrder: Array<{ token: string; type: DatePickerColumnType; len: number }> = [];
    const tokens: Array<{ token: string; type: DatePickerColumnType; len: number }> = [
      { token: 'YYYY', type: 'year', len: 4 },
      { token: 'MM', type: 'month', len: 2 },
      { token: 'DD', type: 'day', len: 2 },
    ];
    tokens.forEach((t) => {
      const pos = fmt.indexOf(t.token);
      if (pos >= 0) tokenOrder.push({ ...t, len: t.len });
    });
    tokenOrder.sort((a, b) => fmt.indexOf(a.token) - fmt.indexOf(b.token));

    const extracted: Record<string, string> = {};
    let cursor = 0;
    let fmtCursor = 0;
    for (const t of tokenOrder) {
      const tokenPos = fmt.indexOf(t.token, fmtCursor);
      const skip = tokenPos - fmtCursor;
      cursor += skip;
      extracted[t.type] = str.slice(cursor, cursor + t.len);
      cursor += t.len;
      fmtCursor = tokenPos + t.token.length;
    }

    return columnsType().map((type: DatePickerColumnType) => extracted[type] || '');
  };

  /** Date 对象 → 内部 string[]，按 columnsType 提取对应字段 */
  const dateToArray = (date: Date): string[] => {
    const types = columnsType();
    return types.map((type: DatePickerColumnType) => {
      if (type === 'year') return String(date.getFullYear());
      if (type === 'month') return String(date.getMonth() + 1).padStart(2, '0');
      return String(date.getDate()).padStart(2, '0');
    });
  };

  /** 内部 string[] → Date 对象（缺失的字段使用 1 补齐） */
  const arrayToDate = (arr: string[]): Date => {
    const year = Number(arr[0]) || new Date().getFullYear();
    const month = Number(arr[1]) || 1;
    const day = Number(arr[2]) || 1;
    return new Date(year, month - 1, day);
  };

  /** 将用户传入的值统一转为内部 string[] 格式 */
  const normalizeToArray = (val: string[] | string | Date): string[] => {
    if (val instanceof Date) return dateToArray(val);
    if (typeof val === 'string') return val ? parseFormatted(val) : [];
    return val ?? [];
  };

  /** 读取当前 v-model 绑定的值，统一返回 string[] */
  const getModelValue = (): string[] => normalizeToArray(getRawValue());

  return {
    getInputMode,
    formatValues,
    normalizeToArray,
    getModelValue,
    dateToArray,
    arrayToDate,
  };
}
